import type { Membership, PortalMode } from './auth/session'

/**
 * Which portal this customer is shown, and what that decides.
 *
 * The platform sells to two audiences out of one product. Most customers are
 * businesses: they want a balance, a way to add to it, what they spent and the
 * token their software needs. Some are development shops, and everything the
 * portal already had was built for them.
 *
 * Rather than scatter `customer.portal_mode === 'service'` through forty
 * components, the mode is read once and turned into a *presentation policy* —
 * a description of what this portal leads with. Components ask the policy a
 * question in their own terms («should technical detail be folded away here?»)
 * and never ask which mode is active.
 *
 * Three rules hold this apart from authorisation, and they are the reason the
 * file is this short:
 *
 *   nothing here returns a capability, and nothing here is consulted by a
 *   permission check — `can(membership, …)` remains the only gate;
 *
 *   nothing here hides a route. Every destination stays reachable by URL in
 *   both modes, because a mode is a default emphasis and not a wall — a
 *   service customer who follows a support link to the request history must
 *   arrive at the request history;
 *
 *   nothing here forks business logic. The same components render both modes
 *   with different emphasis, so a fix to the wallet is a fix to both portals.
 */

/** Professional is the fallback: it shows more, and showing more is the safe
 *  direction to fail when the server has not said. */
export const DEFAULT_MODE: PortalMode = 'professional'

export function portalMode(membership: Membership): PortalMode {
  return membership.customer.portal_mode ?? DEFAULT_MODE
}

export interface Presentation {
  mode: PortalMode
  /** The financial product: balance first, recharge in reach, cost over
   *  request count. */
  isService: boolean
  /** Headers, curl, base URLs, request ids, capability codes: shown plainly in
   *  professional, folded behind a «جزئیات فنی» disclosure in service. Never
   *  removed — the product owner asked for them to stay available. */
  foldsTechnicalDetail: boolean
  /** Whether the sidebar carries the account's balance. Service customers came
   *  for it; professional ones came for the API and have a wallet page. */
  showsBalanceInChrome: boolean
  /** Whether the sandbox and the full documentation are offered as
   *  destinations of their own. */
  offersDeveloperDestinations: boolean
}

export function presentation(membership: Membership): Presentation {
  const mode = portalMode(membership)
  const isService = mode === 'service'
  return {
    mode,
    isService,
    foldsTechnicalDetail: isService,
    showsBalanceInChrome: isService,
    offersDeveloperDestinations: !isService,
  }
}
